import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import * as moment from 'moment';

import { Message } from 'primeng/api';
import { FormBuilderService } from 'src/app/decorator/form-control-config';
import { CanDeactivateComponent } from 'src/app/interface/can-deactivate.interface';
import { API_KEY_RESTRICTIONS } from 'src/app/services/apikey-service';

@Directive()
export class ViewComponent implements CanDeactivateComponent {

  API_KEY_RESTRICTIONS = API_KEY_RESTRICTIONS;

  protected errorMessages: Message[] = [];
  protected form: FormGroup;
  private _router: Router;

  private _fb: FormBuilder;

  private _el: ElementRef;

  @ViewChild('messages') messagesArea!: ElementRef;

  constructor(protected injector: Injector) {
    this._fb = this.injector.get(FormBuilder);
    this._router = this.injector.get(Router);
    this._el = this.injector.get(ElementRef);
  }


  protected scrollToMessages(): void {
    if (this.messagesArea) {
      // this.messagesArea.nativeElement.scrollIntoView({ block: 'start' });
    }
  }

  /**
   * フォームの変更状態をチェック
   * 必要に応じてオーバーライド可能
   */
  canDeactivate(): boolean {
    return !this.form || !this.form.dirty; // 変更がなければ遷移OK
  }


  /**
   * APIから取得したdataの値をformに設定する
   * 
   * コードマスタの項目は setCodeMasterFields() で設定されている項目名と一致した場合にコードマスタ項目と判定され、適切な値が設定される。
   * 
   * @param data 
   * @param form 
   */
  protected updateFormWithData<T extends Record<string, any>>(data: T, form: FormGroup = this.form): void {
    Object.entries(data).forEach(([key, value]) => {
      const control = form.get(key);

      if (Array.isArray(value) && typeof value[0] === 'string') {
        // string[] → FormArray
        const formArray = new FormArray(value.map(v => new FormControl(v)));
        if (form.contains(key)) {
          form.setControl(key, formArray);
        } else {
          form.addControl(key, formArray);
        }
      } else if (control) {
        control.patchValue(value);
      }
    });
  }


  /**
   * formの値からAPIのパラメータに使用するデータオブジェクトを作成する
   * 
   * @param toData form値の設定先データ (newしたインスタンスを設定すること) 
   * @param form toDataへの設定もとフォームグループ
   * @returns 
   */
  protected updateDataWithForm<T extends { [key: string]: any }>(toData: T, form: FormGroup): T {
    Object.keys(form.controls).forEach((key: string) => {
      const control = form.get(key);
      if (control) {
        const value = control.value;

        // FormControlの値がmomentオブジェクトであれば日付文字列に変換
        if (moment.isMoment(value)) {
          // momentオブジェクトからフォーマットされた日付文字列に変換
          (toData as any)[key] = value.format('YYYY-MM-DD');
        } else {
          // それ以外の値はそのまま data に設定
          (toData as any)[key] = value;
          //console.log(key, value);
        }
      }
    });


    return toData;
  }


  private preventEnterSubmit() {
    this._el.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
      // `ENTER` キーが押され、かつフォーム要素内の場合のみ制御
      if (event.key === 'Enter' && this.isInsideForm(event.target as HTMLElement)) {
        event.preventDefault();
      }
    });
  }

  private isInsideForm(element: HTMLElement): boolean {
    return !!element.closest('form');
  }

  /**
   * モデルのインスタンスからFormGroupを生成する。
   * 
   * 本処理内では非同期でコードマスタ等を取得してフォームを生成したりしているため、
   * フォームが完全に生成されてから処理を行う必要がある場合はPromiseを使って処理が完了するまで待つこと。
   * 
   * @param data  ex. Estimateクラスのインスタンス
   * @returns Promise
   */
  protected createFormGroup(data: any): Promise<void> {
    this.preventEnterSubmit();
    return new Promise(async (resolve, reject) => {
      try {
        // console.log("createFormGroup start");
        // console.log(data);
        // フォームグループの作成
        this.form = FormBuilderService.buildForm(this._fb, data);

        // 成功した場合にresolveを呼ぶ
        resolve();
      } catch (error) {
        // エラーが発生した場合はrejectを呼ぶ
        console.error("createFormGroup ", error);
        reject(error);
      }
    });
  }

  protected createFormGroupFor(data: any): Promise<FormGroup> {
    this.preventEnterSubmit();
    return new Promise(async (resolve, reject) => {
      try {
        // フォームグループの作成
        const form = FormBuilderService.buildForm(this._fb, data);

        // 成功した場合にresolveを呼ぶ
        resolve(form);
      } catch (error) {
        // エラーが発生した場合はrejectを呼ぶ
        reject(error);
      }
    });
  }

  protected clearErrorMessage() {
    this.errorMessages = [];
  }

  /**
   * フォームをクリア（まっさらに）する
   */
  protected resetForm() {
    this.resetFormOf(this.form);
  }
  protected resetFormOf(form: FormGroup) {
    form.reset();
    form.updateValueAndValidity();
  }

  /**
   * APIでエラーが発生した場合のレスポンスのエラー内容をフォームコントロールやグローバルエリアメッセージ等に描画する。
   * 
   * @param error APIのエラーレスポンス
   * @param form 描画先のフォームグループ
   */
  protected handlError(error: any, form: FormGroup = this.form) {
    if (typeof error === 'string') {
      this.errorMessages = [{ severity: 'error', detail: error }];
    }

    this.innerHandlError(error);
  }

  protected setFormControlError(controlName: string, errorMessage: string, form: FormGroup = this.form): void {
    const formControl = form.get(controlName);
    if (formControl) {
      formControl.setErrors({ serverError: errorMessage });
    }
  }

  protected innerHandlError(error: any, form: FormGroup = this.form) {
    if (error.error?.message) {
      this.errorMessages = [{ severity: 'error', detail: error.error.message }];
    } else if (error.error?.error) {
      this.errorMessages = [{ severity: 'error', detail: error.error.error }];
    }
    error.error?.details?.forEach((prop: { field: any; message: any; }) => {
      const formControl = form.get(prop.field);
      if (formControl) {
        formControl.setErrors({
          serverError: prop.message
        });
      }
    });
  }

  /**
   * フォームグループ内のすべてのフォームコントロールのdisabled状態を設定する
   * 
   * @param formGroup 
   * @param isEnabled true=活性化 false=非活性
   */
  // フォーム内のすべてのコントロールを状態に応じて設定する
  private setControlsState(formGroup: FormGroup, isEnabled: boolean) {
    if (formGroup && formGroup.controls) {
      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.get(key);
        if (control instanceof FormControl) {
          isEnabled ? control.enable() : control.disable();
        } else if (control instanceof FormGroup) {
          this.setControlsState(control, isEnabled);
        } else if (control instanceof FormArray) {
          control.controls.forEach(c => {
            if (c instanceof FormControl) {
              isEnabled ? c.enable() : c.disable();
            } else if (c instanceof FormGroup) {
              this.setControlsState(c, isEnabled);
            }
          });
        }
      });
    }
  }

  /**
   * フォーム内のすべてのコントロールをdisabledにする.
   * 
   * disbledの状態の時にFormControlへ対する処理を実行しても無効になるから注意
   */
  protected disableAllFormControl() {
    this.setControlsState(this.form, false);
  }

  /**
   * フォーム内のすべてのコントロールをenabledにする/
   * 
   * disbledの状態の時にFormControlへ対する処理を実行しても無効になるから注意
   */
  protected enableAllFormControl() {
    this.setControlsState(this.form, true);
  }





}
